import { showMessage } from "../redux/messages/messagesActions";
import { INotification } from "../redux/notification/notification.types";
import { addNotification } from "../redux/notification/notificationReducer";
import { IOutlookNotification } from "../redux/outlook/outlook.types";
import { addOutlookNotification } from "../redux/outlook/outlookSlice";
import { store } from "../redux/store";
import { socketService } from "./socket";
import { 
  appendToAIMessage, 
  completeAIMessage, 
  setAIMessageError,
  setLoading,
} from "../redux/aiChat/aiChatSlice";



// Maps socket session_id -> Redux message id (for pending AI bubbles)
const sessionIdMap: Record<string, string> = {};

/**
 * Resolves a socket session_id to the Redux message id.
 * On the first delta for a new session, it picks up the window.__pendingAiMessageId__
 * set by ChatWindow.tsx (same pattern as the HTML POC's "pending bubble").
 */
function resolveMessageId(session_id: string): string | null {
  if (sessionIdMap[session_id]) {
    return sessionIdMap[session_id];
  }

  // Associate the pending AI bubble (created by ChatWindow before the fetch)
  const pendingId = (window as any).__pendingAiMessageId__;
  if (pendingId) {
    sessionIdMap[session_id] = pendingId;
    (window as any).__pendingAiMessageId__ = null;
    return pendingId;
  }

  return null;
}

function cleanSession(session_id: string) {
  delete sessionIdMap[session_id];
}

export const registerSocketEvents = () => {
  const socket = socketService.getSocket();

  if (!socket) return;

  socket.on("connect", () => {
    console.log("Connected:", socket.id);
  });

  socket.on("connect_error", (error) => {
    console.error("⚠️ Connection Error:", error);
  });

  socket.on("NOTIFICATION", (data: INotification) => {
    store.dispatch(
      showMessage({
        type: "warning",
        displayAs: "snackbar",
        message: data.message,
      }),
    );
    store.dispatch(addNotification(data));
  });
  socket.on("OUTLOOK_NEW_EMAIL", (data: IOutlookNotification) => {
    store.dispatch(addOutlookNotification(data));

    const sender = data.from_name?.trim();
    const subject = data.subject?.trim();

    let message = "You received a new email";
    if (sender && subject) {
      message = `New email from ${sender}: "${subject}"`;
    } else if (sender) {
      message = `New email from ${sender}`;
    } else if (subject) {
      message = `New email: "${subject}"`;
    }

    store.dispatch(
      showMessage({
        type: "warning",
        displayAs: "snackbar",
        message,
      }),
    );
  });

  // ── AI Chat Streaming Events ──────────────────────────────────────────

  socket.on('AI_STREAM_DELTA', ({ session_id, delta }: { session_id: string; delta: string }) => {
    console.log('[AI_STREAM_DELTA]', session_id, delta);
    const messageId = resolveMessageId(session_id);
    if (messageId) {
      store.dispatch(appendToAIMessage({ id: messageId, delta }));
    }
  });

  socket.on('AI_STREAM_END', ({ session_id, final_output }: { session_id: string; final_output: string }) => {
    console.log('[AI_STREAM_END]', session_id);
    const messageId = resolveMessageId(session_id);
    if (messageId) {
      store.dispatch(completeAIMessage({ id: messageId, final_output }));
    }
    store.dispatch(setLoading(false));
    cleanSession(session_id);
  });

  socket.on('AI_STREAM_ERROR', ({ session_id, message }: { session_id: string; message: string }) => {
    console.error('[AI_STREAM_ERROR]', session_id, message);
    const messageId = resolveMessageId(session_id);
    if (messageId) {
      store.dispatch(setAIMessageError({ id: messageId, message }));
    }
    store.dispatch(setLoading(false));
    store.dispatch(
      showMessage({
        type: "error",
        displayAs: "snackbar",
        message: message || "AI Assistant encountered an error",
      }),
    );
    cleanSession(session_id);
  });
};

